import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Activity } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { ActivityForm } from "../ActivityForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListActivities.scss";

const activityController = new Activity();

export function ListActivities({ activitiesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const [activities,       setActivities]       = useState(null);
  const [activitiesFilter, setActivitiesFilter] = useState([]);
  const [search,           setSearch]           = useState("");
  const [showModal,        setShowModal]        = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showConfirm,      setShowConfirm]      = useState(false);
  const [confirmMsg,       setConfirmMsg]       = useState("");
  const [isDelete,         setIsDelete]         = useState(false);
  const [sortAsc,          setSortAsc]          = useState(true);
  const [rowMenu,          setRowMenu]          = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".activities-list__row-menu, .activities-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setActivities(null);
        const res = await activityController.getActivities(accessToken, activitiesActive);
        setActivities(res);
        setActivitiesFilter(res);
      } catch {
        setActivities([]);
        setActivitiesFilter([]);
      }
    })();
  }, [activitiesActive, reload]);

  useEffect(() => {
    if (!activities) return;
    if (!search) { setActivitiesFilter(activities); return; }
    const q = search.toLowerCase();
    setActivitiesFilter(
      activities.filter((a) =>
        a.code?.toLowerCase().includes(q) ||
        a.name?.toLowerCase().includes(q) ||
        a.area?.name?.toLowerCase().includes(q)
      )
    );
  }, [search, activities]);

  const openCreate = () => { setSelectedActivity(null); setShowModal(true); };
  const openEdit   = (a)  => { setSelectedActivity(a);   setShowModal(true); };

  const openToggle = (a) => {
    setSelectedActivity(a);
    setIsDelete(false);
    setConfirmMsg(`¿${a.active ? "Desactivar" : "Activar"} "${a.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (a) => {
    setSelectedActivity(a);
    setIsDelete(true);
    setConfirmMsg(`¿Eliminar "${a.name}"? Esta acción no se puede deshacer.`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await activityController.updateActivity(accessToken, selectedActivity._id, { active: !selectedActivity.active });
      toast.success(selectedActivity.active ? "Actividad desactivada" : "Actividad activada");
      setShowConfirm(false);
      onReload();
    } catch { toast.error("Error al actualizar"); }
  };

  const handleDelete = async () => {
    try {
      await activityController.deleteActivity(accessToken, selectedActivity._id);
      toast.success("Actividad eliminada");
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || "Error al eliminar"); }
  };

  if (activities === null) return <LoadingSpinner />;

  return (
    <div className="activities-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="activities-list__card">
        <div className="activities-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-activity-btn" title="Nueva actividad" onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {activitiesFilter.length === 0 ? (
          <div className="list-empty-state">
            <Icon name="folder open outline" className="list-empty-state__icon" />
            <p className="list-empty-state__text">Sin resultados</p>
          </div>
        ) : (
          <div className="activities-list__tbl-scroll">
            <Table className="activities-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  {/* <Table.HeaderCell>Código</Table.HeaderCell> */}
                  <Table.HeaderCell
                    sorted={sortAsc ? "ascending" : "descending"}
                    onClick={() => {
                      const asc = !sortAsc;
                      setSortAsc(asc);
                      setActivitiesFilter((prev) =>
                        [...prev].sort((a, b) =>
                          asc
                            ? (a.area?.name || "").localeCompare(b.area?.name || "")
                            : (b.area?.name || "").localeCompare(a.area?.name || "")
                        )
                      );
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    Área
                  </Table.HeaderCell>
                  <Table.HeaderCell>Nombre</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {activitiesFilter.map((a) => (
                  <Table.Row key={a._id}>
                    {/* <Table.Cell>{a.code}</Table.Cell> */}
                    <Table.Cell data-label="Área">{a.area?.name || "—"}</Table.Cell>
                    <Table.Cell data-label="Nombre">{a.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="activities-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="activities-list__row-menu">
                        <button type="button" className="activities-list__row-menu-btn" onClick={(e) => toggleRowMenu(a._id, e)} aria-label="Acciones">
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === a._id && ReactDOM.createPortal(
                        <div
                          className="activities-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(a); setRowMenu(null); }}>
                            <Icon name="pencil" />Editar
                          </button>
                          <button onClick={() => { openToggle(a); setRowMenu(null); }}>
                            <Icon name={a.active ? "ban" : "check"} />{a.active ? "Desactivar" : "Activar"}
                          </button>
                          <button className="activities-list__row-menu-item--danger" onClick={() => { openDelete(a); setRowMenu(null); }}>
                            <Icon name="trash" />Eliminar
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedActivity ? `Editar: ${selectedActivity.name}` : "Nueva Actividad"}
        size="small"
        className="catalog-modal"
      >
        <ActivityForm
          activity={selectedActivity}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedActivity?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
        confirmDanger={isDelete}
      />
    </div>
  );
}
